import { BrowserProvider, Contract, formatUnits, parseUnits, keccak256, toUtf8Bytes, ZeroAddress, isAddress, toBeHex } from "ethers";
import pusdAbi from "./pusdAbi.json";

export const ROLES = {
  DEFAULT_ADMIN_ROLE: "0x" + "00".repeat(32),
  UPGRADER_ROLE: keccak256(toUtf8Bytes("UPGRADER_ROLE")),
  MASTER_MINTER_ROLE: keccak256(toUtf8Bytes("MASTER_MINTER_ROLE")),
  MINTER_ROLE: keccak256(toUtf8Bytes("MINTER_ROLE")),
  PAUSER_ROLE: keccak256(toUtf8Bytes("PAUSER_ROLE")),
  BLACKLISTER_ROLE: keccak256(toUtf8Bytes("BLACKLISTER_ROLE")),
  BURNER_ROLE: keccak256(toUtf8Bytes("BURNER_ROLE")),
};

export const ROLE_LABELS = {
  [ROLES.DEFAULT_ADMIN_ROLE]: "Owner",
  [ROLES.UPGRADER_ROLE]: "Upgrader",
  [ROLES.MASTER_MINTER_ROLE]: "Master Minter",
  [ROLES.MINTER_ROLE]: "Minter",
  [ROLES.PAUSER_ROLE]: "Pauser",
  [ROLES.BLACKLISTER_ROLE]: "Blacklister",
  [ROLES.BURNER_ROLE]: "Burner",
};

export const ROLE_KEYS = Object.keys(ROLE_LABELS);

export function hasEthereum() {
  return typeof window !== "undefined" && !!window.ethereum;
}

/**
 * Pick the best injected provider when several wallets are installed.
 * EIP-5749 / EIP-6963 style: window.ethereum.providers is an array when
 * multiple wallets are present.
 */
export function getInjectedProvider() {
  if (typeof window === "undefined" || !window.ethereum) return null;
  const eth = window.ethereum;
  if (Array.isArray(eth.providers) && eth.providers.length > 0) {
    return (
      eth.providers.find((p) => p.isMetaMask && !p.isBraveWallet) ||
      eth.providers.find((p) => p.isMetaMask) ||
      eth.providers[0]
    );
  }
  return eth;
}

export function detectedWalletName() {
  const p = getInjectedProvider();
  if (!p) return null;
  if (p.isMetaMask) return "MetaMask";
  if (p.isCoinbaseWallet || p.isCoinbase) return "Coinbase Wallet";
  if (p.isTrust) return "Trust Wallet";
  if (p.isRabby) return "Rabby";
  if (p.isBraveWallet) return "Brave Wallet";
  if (p.isPhantom) return "Phantom";
  return "Injected Wallet";
}

export async function ensureChain({ chainId, rpcUrl, networkName, explorerUrl, symbol = "ETH" }, providerOverride) {
  const eth = providerOverride || getInjectedProvider();
  if (!chainId || !eth) return;
  const hex = toBeHex(chainId);
  try {
    await eth.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: hex }],
    });
  } catch (switchErr) {
    // 4902 = unrecognized chain → add it
    if (switchErr?.code === 4902 || /Unrecognized chain ID/i.test(switchErr?.message || "")) {
      if (!rpcUrl) throw new Error(`Network chain ${chainId} is not added in your wallet, and no RPC URL is set in Settings.`);
      await eth.request({
        method: "wallet_addEthereumChain",
        params: [{
          chainId: hex,
          chainName: networkName || `Chain ${chainId}`,
          rpcUrls: [rpcUrl],
          nativeCurrency: { name: symbol, symbol, decimals: 18 },
          blockExplorerUrls: explorerUrl ? [explorerUrl] : undefined,
        }],
      });
    } else {
      throw switchErr;
    }
  }
}

export async function connectWallet({ chainId, rpcUrl, networkName, explorerUrl } = {}) {
  const eth = getInjectedProvider();
  if (!eth) {
    const err = new Error("No injected wallet detected. Install MetaMask from https://metamask.io, then refresh.");
    err.code = "NO_WALLET";
    throw err;
  }
  if (chainId) {
    await ensureChain({ chainId, rpcUrl, networkName, explorerUrl }, eth);
  }
  let accounts;
  try {
    accounts = await eth.request({ method: "eth_requestAccounts" });
  } catch (e) {
    // Some hijacking extensions throw "MetaMask extension not found" when MM isn't installed.
    const msg = e?.message || "";
    if (/MetaMask.*not found/i.test(msg) || /not installed/i.test(msg)) {
      const err = new Error("Detected wallet wants to use MetaMask, but MetaMask isn't installed. Install MetaMask from https://metamask.io or disable other wallet extensions and refresh.");
      err.code = "NO_METAMASK";
      throw err;
    }
    throw e;
  }
  if (!accounts || accounts.length === 0) {
    throw new Error("Wallet returned no accounts. Unlock your wallet and try again.");
  }
  const provider = new BrowserProvider(eth);
  const signer = await provider.getSigner();
  const network = await provider.getNetwork();
  return { provider, signer, address: accounts[0], chainId: Number(network.chainId) };
}

export function getReadContract(provider, address) {
  return new Contract(address, pusdAbi, provider);
}

export function getWriteContract(signer, address) {
  return new Contract(address, pusdAbi, signer);
}

export { pusdAbi, formatUnits, parseUnits, ZeroAddress, isAddress };
